/**
 * Server state of «Юрлица»: the list, the card, and the card's writes. Field writes patch the card
 * in the cache at once and roll back on error; every write then refreshes all screens (gaps,
 * month screens and other cards read entities too).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  EntitiesResponse,
  EntityBankUpdate,
  EntityCard,
  EntityUpdate,
  EntityWriteResult,
  SeveranceOption,
} from '@outsource/shared';
import { api } from '../../lib/api';
import { showErrorToast } from '../../lib/toast';

export const entityKeys = {
  list: ['entities'] as const,
  card: (id: string) => ['entities', id] as const,
};

const path = (id: string) => `/entities/${encodeURIComponent(id)}`;

export function useEntitiesQuery() {
  return useQuery({ queryKey: entityKeys.list, queryFn: () => api.get<EntitiesResponse>('/entities') });
}

export function useEntityQuery(id: string) {
  return useQuery({ queryKey: entityKeys.card(id), queryFn: () => api.get<EntityCard>(path(id)) });
}

const errorText = (error: unknown): string =>
  error instanceof Error ? error.message : 'Не удалось сохранить';

/** A card write: optimistic patch, rollback and an error toast on failure, refresh after. */
function useCardWrite<TInput>(
  id: string,
  request: (input: TInput) => Promise<EntityWriteResult>,
  patchCard?: (card: EntityCard, input: TInput) => EntityCard,
) {
  const queryClient = useQueryClient();
  const key = entityKeys.card(id);
  return useMutation({
    mutationFn: request,
    onMutate: async (input: TInput) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<EntityCard>(key);
      if (previous && patchCard) queryClient.setQueryData(key, patchCard(previous, input));
      return { previous };
    },
    onError: (error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
      showErrorToast(errorText(error));
    },
    onSettled: () => queryClient.invalidateQueries(),
  });
}

export function useUpdateEntity(id: string) {
  return useCardWrite(
    id,
    (change: EntityUpdate) => api.patch<EntityWriteResult>(path(id), change),
    (card, change) => ({ ...card, [change.field]: change.value }),
  );
}

interface BankChange {
  readonly bankId: string;
  readonly change: EntityBankUpdate;
}

export function useUpdateBank(id: string) {
  return useCardWrite(
    id,
    ({ bankId, change }: BankChange) =>
      api.patch<EntityWriteResult>(`${path(id)}/banks/${encodeURIComponent(bankId)}`, change),
    (card, { bankId, change }) => ({
      ...card,
      banks: card.banks.map((bank) =>
        bank.id === bankId ? { ...bank, [change.field]: change.value } : bank,
      ),
    }),
  );
}

export function useAddBank(id: string) {
  return useCardWrite(id, () => api.post<EntityWriteResult>(`${path(id)}/banks`));
}

export function useRemoveBank(id: string) {
  return useCardWrite(
    id,
    (bankId: string) => api.del<EntityWriteResult>(`${path(id)}/banks/${encodeURIComponent(bankId)}`),
    (card, bankId) => ({ ...card, banks: card.banks.filter((bank) => bank.id !== bankId) }),
  );
}

export function useLinkPerson(id: string) {
  return useCardWrite(id, (personId: string) =>
    api.post<EntityWriteResult>(`${path(id)}/people`, { personId }),
  );
}

export interface DismissInput {
  readonly personId: string;
  readonly lastDay: string;
  readonly severance: SeveranceOption;
}

/** «Уволить» (V175, V292): the person leaves the entity after the last working day, with or without severance. */
export function useDismissPerson(id: string) {
  return useCardWrite(id, ({ personId, lastDay, severance }: DismissInput) =>
    api.post<EntityWriteResult>(`${path(id)}/people/${encodeURIComponent(personId)}/dismiss`, { lastDay, severance }),
  );
}

export function useCreateEntity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<EntityWriteResult>('/entities'),
    onError: (error) => showErrorToast(errorText(error)),
    onSettled: () => queryClient.invalidateQueries(),
  });
}
